export type Maybe<T> = T | null

export type Scalars = {
  ID: number
  Boolean: boolean
  String: string
  Int: number
}

export type AuthUserBase = {
  id: number
  userType: "client" | "deal_manager"
}

export type AuthUser = Client | DealManager

export type Client = AuthUserBase & {
  id: Scalars['ID']
  userType: "client"
  firstName: Scalars['String']
  lastName: Scalars['String']
  email: Scalars['String']
  phoneNumber?: Scalars['String']
  esoId?: Maybe<Scalars['Int']>
  createdDate: Scalars['String']
  status?: Scalars["String"]
  country?: Scalars["String"]
  addressLine1?: Scalars["String"]
  city?: Scalars["String"]
  state?: Scalars["String"]
  zip?: Scalars["String"]
  isForeign?: Scalars["Boolean"]
  paymentCenterActive?: Scalars["Int"]
  isPhoneVerified?: Scalars["Int"]
  twoFactorEnabled?: Scalars["Int"]
  // DM-enabled escape hatch: 1 → show the manual bank-entry tab in the Payment Center.
  manualEntryEnabled?: Scalars["Int"]
  // Limited-rollout gate: false → client only gets the Payment Center (+ account/2FA)
  // until their intro/welcome email is sent. Computed server-side (auth/me + login).
  fullPortalAccess?: Scalars["Boolean"]
  deals: Deal[]
  dealManager: DealManager
}

export type EsoClient = {
  id: Scalars['ID']
  firstName: Scalars['String']
  lastName: Scalars['String']
  email: Scalars['String']
  phoneNumber: Scalars['String']
  // The client's ESO id — the payments-server client_number. Required by the
  // view-as-client Payment Center.
  esoId?: Maybe<Scalars['Int']>
  createdDate: Scalars['String']
  paymentCenterActive?: Scalars['Int']
  manualEntryEnabled?: Scalars['Int']
  fullPortalAccess?: Scalars['Boolean']
  deals: Deal[]
  dealManager: DealManager
}

export type Company = {
  id: Scalars['ID']
  name: Scalars['String']
  fundingRounds: CompanyFundingRound[]
}

export type CompanyFundingRound = {
  price: Scalars['Int']
  date: Scalars['String']
  round: Scalars['String']
}

export type DashboardData = {
  currentMonthCount: Scalars['Int']
  percentChange: Scalars['Int']
}

export type Deal = {
  id: Scalars['ID']
  esoId?: number
  company: Company
  offers: Offer[]
  dealManager: DealManager
  stage: OfferStatus
  client: Client
  isActive: Scalars['Int']
  currentStep: DealStep
  dealType?: DealType
  pendingAmount?: number
  pendingDescription?: string
  sentAmount?: number
  outstandingAmount?: number
}

export type DealType = "Traditional" | "Installment Sale" | "Deemed Sale w/Buy Back" | "Forward Contract" | "IPO Lockup or Term Loan" | "N/A" | "RSU" | "Transfer"

export type DealTrend = {
  month: Scalars["Int"]
  count: Scalars["Int"]
}

export type CompanyDealData = {
  companyName: Scalars['String']
  dealCount: Scalars['Int']
}

export type DealManager = AuthUserBase & {
  id: Scalars['ID']
  userType: "deal_manager"
  firstName: Scalars['String']
  lastName: Scalars['String']
  email: Scalars['String']
  profilePicture: Scalars['String']
  phoneNumber: Scalars['String']
  calendly: Scalars['String']
}

export type CoveredEquity = {
  coveredEquityAmount: Scalars['Int']
  equity: Equity
}

export type Equity = {
  name: Scalars['String']
  issueDate: Scalars['String'] | null
  type: EquityType,
  amount: Scalars['Int']
  strikePrice: Scalars['Int']
  cost: Scalars['Int']
  tax: Scalars['Int']
}

export type StepDetail = {
  step: DealStep
  title: Scalars['String']
  description: Scalars['String']
  completed: Scalars['Boolean']
  isActive: Scalars['Boolean']
}

export type Offer = {
  id: Scalars['ID']
  exerciseCost: Maybe<Scalars['Int']>
  desiredLiquidity: Scalars['Int']
  pp: Scalars['Int']
  lp: Scalars['Int']
  isActive: Scalars['Int']
  isAccepted: Scalars['Int']
  isIncentive: Scalars['Int']
  incentivePp: Maybe<Scalars['Int']>
  incentiveLp: Maybe<Scalars['Int']>
  incentiveDeadline: Maybe<Scalars['String']>
  incentiveType: Maybe<IncentiveType>
  hypotheticalSharePrice: Scalars['Int']
  equities: CoveredEquity[]
  offerNumber: Scalars['Int']
}

export type UploadedDocument = {
  name: Scalars['String']
  type: Scalars['String']
  uploadedBy: Scalars['String']
  date: Scalars['String']
}

export type Resource = {
  id: Scalars['ID']
  title: Scalars['String']
  url: Scalars['String']
  photo?: Scalars['String']
  excerpt?: Scalars['String']
}

export type TaxCalculation = {
  amount: Scalars['Int']
  strikePrice: Scalars['Int']
  type: StockOptionType,
}

export type TaxData = {
  year: Scalars['Int']
  marriageStatus: Scalars['String']
  bracket: Scalars['String']
  fedRate: Scalars['Int']
  fedLevel: Scalars['Int']
  fedOwe: Scalars['Int']
  fedDeduction: Scalars['Int']
  exemption: Scalars['Int']
  fedAmtLevel: Scalars['Int']
  amtThreshold: Scalars['Int']
  socSec: Scalars['Int']
  medicare: Scalars['Int']
}

export type Step = {
  title: string
  description: string
  completed: boolean
  isActive: boolean
}

export type StateTaxData = {
  year: Scalars['Int']
  state: Scalars['String']
  marriageStatus: Scalars['String']
  bracket: Scalars['String']
  stateRate: Scalars['Int']
  stateLevel: Scalars['Int']
  stateOwe: Scalars['Int']
  stateDeduction: Scalars['Int']
  stateAmtRate: Scalars['Int']
}

export enum EquityType {
  ISO = "iso",
  NSO = "nso",
  SHARE = "share",
  RSU = "rsu"
}

export enum IncentiveType {
  TWO_YEARS = "two_years",
  ONE_YEAR = "one_year",
  NINE_MONTHS = "nine_months",
  SIX_MONTHS = "six_months",
  THREE_MONTHS = "three_months",
}

export enum OfferStatus {
  INTRO = "intro",
  ESO_REVIEW = "eso_review",
  OFFER_READY = "offer_ready",
  PRE_CLOSING = "pre_closing",
  CONTRACT_REVIEW = "contract_review",
  POST_CLOSING = "post_closing",
  FULLY_FUNDED = "fully_funded",
  LIQUIDITY = "liquidity",
  CLOSED = "closed",
}

export enum StockOptionType {
  ISO = "iso",
  NSO = "nso",
}

export enum NavOption {
  ACCOUNT = "account",
  COMPLIANCE = "compliance",
  DOCUMENTS = "documents",
  CURRENT_OFFER = "your_deals",
  RESOURCES = "resources",
  TAX_CALCULATION = "tax_calculation",
  DASHBOARD = "dashboard",
  CLIENT = "client",
  NDA = "nda",
  CLIENTS = "clients",
  PAYMENT_CENTER = "payment_center",
  SECONDARY = "secondary",
  DEALS = "eso/deals",
    // BACKGROUND_CHECK = "background_check"
}

export enum ComplianceType {
  BACKGROUND_CHECK = "Background Check",
  NDA = "NDA"
}

export enum DocumentType {
  DEAL = "deal",
  COMPLIANCE = "compliance"
}

export enum DealStep {
  COMPLETE_NDA_FORM = "complete_nda_form",
  SIGN_NDA = "sign_nda",
  UPLOAD_GRANT_DOCS = "upload_grant_docs",
  ACCEPT_OFFER = "accept_offer",
  COMPLETE_BC_FORM = "complete_bc_form",
  SIGN_BC = "sign_bc",
  UPLOAD_CLOSING_DOCS = "upload_closing_docs",
  CLOSING = "closing",
  CLOSED = "closed",
}